import React, { useState, useEffect } from 'react';
import MetricCard from './MetricCard';
import ChartCard from './ChartCard';
import ActivityFeed from './ActivityFeed';
import { apiService } from '../../config/api';

interface AdminDashboardProps {
  className?: string;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ className = '' }) => {
  const [metrics, setMetrics] = useState<any>({});
  const [revenueData, setRevenueData] = useState<any[]>([]);
  const [vendorGrowthData, setVendorGrowthData] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    loadDashboardData();
  }, [period]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load metrics
      const metricsResponse = await apiService.dashboard.getMetrics(period);
      setMetrics(metricsResponse.data.data);

      // Load chart data
      const [revenueResponse, vendorResponse, activityResponse] = await Promise.all([
        apiService.dashboard.getChartData('revenue', period),
        apiService.dashboard.getChartData('vendors', period),
        apiService.dashboard.getRecentActivity(10)
      ]);

      setRevenueData(revenueResponse.data.data?.data || []);
      setVendorGrowthData(vendorResponse.data.data?.data || []);
      setActivities(activityResponse.data.data?.activities || []);
    } catch (error) {
      console.error('Error loading admin dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-black dark:text-white">
            Admin Dashboard
          </h2>
          <p className="text-meta-6">
            Overview of your business performance and key metrics
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="rounded border border-stroke bg-transparent py-2 px-3 text-black dark:text-white dark:border-form-strokedark dark:bg-form-input focus:border-primary focus:outline-none"
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Revenue"
          value={metrics.totalRevenue || 0}
          change={metrics.revenueGrowth}
          changeLabel="vs previous period"
          valuePrefix="₹"
          loading={loading}
          icon={
            <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          }
        />

        <MetricCard
          title="Active Vendors"
          value={metrics.activeVendors || 0}
          change={metrics.vendorGrowth}
          changeLabel="vs previous period"
          loading={loading}
          icon={
            <svg className="w-6 h-6 text-meta-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4" />
            </svg>
          }
        />

        <MetricCard
          title="Total Orders"
          value={metrics.totalOrders || 0}
          loading={loading}
          icon={
            <svg className="w-6 h-6 text-meta-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 0 0-8 0v4M5 9h14l1 12H4L5 9Z" />
            </svg>
          }
        />

        <MetricCard
          title="Active QR Codes"
          value={metrics.activeQrCodes || 0}
          loading={loading}
          icon={
            <svg className="w-6 h-6 text-meta-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1Zm12 0h2a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1ZM5 20h2a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1Z" />
            </svg>
          }
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard
          title="Revenue Trend"
          data={revenueData}
          type="area"
          loading={loading}
          xDataKey="date"
          yDataKey="revenue"
          color="#3C50E0"
          dateFormat="daily"
          valueType="currency"
          formatTooltip={(value, name) => [`₹${value.toLocaleString()}`, 'Revenue']}
        />

        <ChartCard
          title="Vendor Growth"
          data={vendorGrowthData}
          type="line"
          loading={loading}
          xDataKey="date"
          yDataKey="vendors"
          color="#10B981"
          dateFormat="daily"
          valueType="number"
          formatTooltip={(value, name) => [`${value} vendors`, 'New Vendors']}
        />
      </div>

      {/* System Health & Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* System Health Metrics */}
        <div className="rounded-sm border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
          <h4 className="text-xl font-semibold text-black dark:text-white mb-4">
            System Health
          </h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-meta-6">QR Code Utilization</span>
              <span className="text-sm font-medium text-black dark:text-white">
                {metrics.qrCodeUtilization || 0}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300" 
                style={{ width: `${metrics.qrCodeUtilization || 0}%` }}
              ></div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-meta-6">Subscriptions Expiring Soon</span>
              <span className="text-sm font-medium text-meta-1">
                {metrics.subscriptionsExpiringSoon || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <ActivityFeed
            activities={activities}
            loading={loading}
            title="Recent System Activity"
            maxItems={8}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;